import { useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ImagePlus, Plus, X, Loader2 } from "lucide-react";
import { api, errMsg, imgUrl, currencyInfo } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const WARN = "ring-2 ring-amber-400 bg-amber-50/40";

const Box = ({ label, warn, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="flex items-center gap-1">{label}{warn && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}</Label>
    {children}
    {hint && <p className={`text-xs ${warn ? "text-amber-700" : "text-gray-500"}`}>{hint}</p>}
  </div>
);

const Images = ({ images, setImages }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/seller/uploads", fd);
      setImages([...images, data.url]);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap gap-2">
      {images.map((u) => (
        <div key={u} className="relative"><img src={imgUrl(u)} alt="" className="h-20 w-20 rounded-lg object-cover" />
          <button type="button" onClick={() => setImages(images.filter((x) => x !== u))} className="absolute -right-1.5 -top-1.5 rounded-full bg-gray-900 p-0.5 text-white"><X className="h-3 w-3" /></button></div>
      ))}
      <button type="button" data-testid="form-add-image" onClick={() => ref.current.click()} className="grid h-20 w-20 place-items-center rounded-lg border-2 border-dashed text-gray-400 hover:border-orange-400">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
      </button>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => upload(e.target.files[0])} />
    </div>
  );
};

const ListEditor = ({ items, setItems, a, b, pa, pb, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    {items.map((it, i) => (
      <div key={i} className="flex gap-2">
        <Input value={it[a]} placeholder={pa} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, [a]: e.target.value } : x)))} />
        <Input value={it[b]} placeholder={pb} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, [b]: e.target.value } : x)))} />
        <Button type="button" variant="ghost" size="icon" onClick={() => setItems(items.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>
      </div>
    ))}
    <Button type="button" variant="outline" size="sm" onClick={() => setItems([...items, { [a]: "", [b]: "" }])} data-testid={`${testid}-add`}><Plus className="mr-1 h-3 w-3" />Ajouter</Button>
  </div>
);

export const emptyProduct = { title: "", description: "", category_slug: "", subcategory: "", price: "", old_price: "", stock: "", images: [], features: [], keywords: [], variants: [], sales_pitch: "", ai_generated: false };

export default function ProductForm({ initial, unknown = [], onSubmit, externalImage }) {
  const { data: cats } = useApi("/categories");
  const { shop } = useOutletContext() || {};
  const { label: curLabel, decimals } = currencyInfo(shop?.currency);
  const step = decimals ? 1 / 10 ** decimals : 1;
  const [f, setF] = useState({ ...emptyProduct, ...initial, variants: (initial?.variants || []).map((v) => ({ name: v.name, options: (v.options || []).join(", ") })), keywords: (initial?.keywords || []).join(", ") });
  const [rights, setRights] = useState(!!initial?.image_rights_confirmed);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const unk = unknown.map((u) => u.toLowerCase());
  const cat = cats?.find((c) => c.slug === f.category_slug);

  const save = async (status) => {
    if (!f.price || Number(f.price) <= 0) return toast.error("Indiquez votre prix de vente");
    if (f.stock === "" || Number(f.stock) < 0) return toast.error("Indiquez le stock disponible");
    if (!f.category_slug) return toast.error("Choisissez une catégorie");
    if (status === "published" && !confirmed) return toast.error("Cochez la confirmation avant de publier");
    if (status === "published" && f.images.length && !rights) return toast.error("Confirmez vos droits sur les images");
    setBusy(true);
    const payload = {
      ...f, price: Number(f.price), stock: Number(f.stock), old_price: f.old_price ? Number(f.old_price) : null, subcategory: f.subcategory || null,
      keywords: f.keywords.split(",").map((k) => k.trim()).filter(Boolean),
      variants: f.variants.filter((v) => v.name).map((v) => ({ name: v.name, options: v.options.split(",").map((o) => o.trim()).filter(Boolean) })),
      features: f.features.filter((x) => x.label && x.value), status, image_rights_confirmed: rights, seller_confirmed: confirmed,
    };
    try { await onSubmit(payload); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div data-testid="product-form" className="space-y-6">
      {unknown.length > 0 && (
        <div data-testid="ai-unknown-fields" className="rounded-xl bg-amber-50 p-4 text-sm text-amber-900 ring-1 ring-amber-300">
          <p className="mb-1 flex items-center gap-2 font-bold"><AlertTriangle className="h-4 w-4" />Informations non vérifiables à compléter ou vérifier</p>
          <ul className="list-inside list-disc">{unknown.map((u) => <li key={u}>{u}</li>)}</ul>
        </div>
      )}
      <div className="grid gap-5 rounded-2xl border bg-white p-5 md:grid-cols-2">
        <div className="md:col-span-2"><Box label="Photos"><Images images={f.images} setImages={(images) => setF({ ...f, images })} /></Box></div>
        <div className="md:col-span-2"><Box label="Titre"><Input data-testid="form-title" value={f.title} onChange={set("title")} /></Box></div>
        <div className="md:col-span-2"><Box label="Description"><Textarea data-testid="form-description" rows={6} value={f.description} onChange={set("description")} /></Box></div>
        <Box label="Catégorie" warn={!f.category_slug}>
          <Select value={f.category_slug || ""} onValueChange={(v) => setF({ ...f, category_slug: v, subcategory: "" })}>
            <SelectTrigger data-testid="form-category" className={!f.category_slug ? WARN : ""}><SelectValue placeholder="Choisir" /></SelectTrigger>
            <SelectContent>{cats?.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </Box>
        <Box label="Sous-catégorie">
          <Select value={f.subcategory || ""} onValueChange={(v) => setF({ ...f, subcategory: v })}>
            <SelectTrigger data-testid="form-subcategory"><SelectValue placeholder="Choisir" /></SelectTrigger>
            <SelectContent>{(cat?.subcategories || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </Box>
        <Box label={`Prix de vente (${curLabel})`} warn={!f.price} hint={`À renseigner par vous. L'IA ne propose jamais de prix.${decimals ? ` Jusqu'à ${decimals} décimale(s).` : " Montant entier."}`}><Input data-testid="form-price" type="number" min={step} step={step} className={!f.price ? WARN : ""} value={f.price} onChange={set("price")} /></Box>
        <Box label="Ancien prix (facultatif, promo)"><Input data-testid="form-old-price" type="number" min="0" step={step} value={f.old_price || ""} onChange={set("old_price")} /></Box>
        <Box label="Stock disponible" warn={f.stock === ""} hint="À renseigner par vous."><Input data-testid="form-stock" type="number" min="0" className={f.stock === "" ? WARN : ""} value={f.stock} onChange={set("stock")} /></Box>
        <Box label="Mots-clés (séparés par des virgules)"><Input data-testid="form-keywords" value={f.keywords} onChange={set("keywords")} /></Box>
        <div className="md:col-span-2"><Box label="Caractéristiques" warn={unk.some((u) => !["prix", "stock", "catégorie"].some((k) => u.includes(k)))} hint="Ne gardez que des informations exactes."><ListEditor testid="form-features" items={f.features} setItems={(features) => setF({ ...f, features })} a="label" b="value" pa="Caractéristique" pb="Valeur" /></Box></div>
        <div className="md:col-span-2"><Box label="Variantes (ex : Taille → S, M, L)"><ListEditor testid="form-variants" items={f.variants} setItems={(variants) => setF({ ...f, variants })} a="name" b="options" pa="Nom (Taille, Couleur…)" pb="Options séparées par des virgules" /></Box></div>
        <div className="md:col-span-2"><Box label="Présentation commerciale"><Textarea data-testid="form-pitch" rows={3} value={f.sales_pitch || ""} onChange={set("sales_pitch")} /></Box></div>
      </div>
      <div className="space-y-3 rounded-2xl border bg-white p-5 text-sm">
        {f.images.length > 0 && (
          <label className="flex items-start gap-3"><Checkbox data-testid="form-rights" checked={rights} onCheckedChange={(v) => setRights(!!v)} />
            <span>Je confirme avoir le droit d'utiliser ces images{externalImage ? " (une image a été importée depuis un site externe)" : ""}.</span></label>
        )}
        <label className="flex items-start gap-3"><Checkbox data-testid="form-confirm" checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} />
          <span>J'ai vérifié toutes les informations de cette fiche (titre, description, caractéristiques, prix, stock) et je confirme qu'elles sont exactes.</span></label>
        <div className="flex flex-wrap gap-3 pt-2">
          <Button variant="outline" disabled={busy} onClick={() => save("draft")} data-testid="form-save-draft" className="rounded-full">Enregistrer en brouillon</Button>
          <Button disabled={busy} onClick={() => save("published")} data-testid="form-publish" className="rounded-full">Publier le produit</Button>
        </div>
      </div>
    </div>
  );
}
